import { useMutation, useQueryClient } from '@tanstack/react-query';

import { REVIEW_ACTION_ITEMS_QUERY_KEY } from '~/services/review/';
import { useHttpClient } from '~/hooks/useHttpClient';
import { createLogger } from '~/utils/logging';
import { sanitizeTagValue } from '~/utils/instrumentation';
import { ClientError } from '~/errors';

const log = createLogger('useDraftMessageMutation');

export type DraftMessageRequest = {
  actionItemId?: string;
};

export type DraftMessageResponse = {
  message: {
    actionItemId: string;
    body: string;
  };
};

export function useDraftMessageMutation() {
  const queryClient = useQueryClient();
  const httpClient = useHttpClient();

  const { data, error, mutate, reset, status } = useMutation({
    mutationFn: async (request: DraftMessageRequest) => {
      log.debug('Drafting message', request);
      const { message } = await httpClient.post<DraftMessageResponse>('/review/draftMessage', request);
      log.info('Drafted message successfully', {
        actionItemId: message.actionItemId,
        messageBodyLength: message.body.length,
      });
      return { message };
    },
    onSuccess: () => {
      // TODO: Display a toast notification
      queryClient.invalidateQueries({ queryKey: [REVIEW_ACTION_ITEMS_QUERY_KEY] });
    },
    onError: (error: Error) => {
      if (error instanceof ClientError) {
        // Ignore client errors.
      } else {
        log.error('Failed to draft message', {
          errorMessage: sanitizeTagValue(error.message),
        });
      }
    },
  });

  return {
    error,
    mutate,
    reset,
    status,
    message: data?.message,
  };
}
